import get from "lodash.get";

import {
  CreateExclusionGroupEntityFe,
  ExclusionGroupEntityDto,
  MetricArrayType,
} from "@intelligems/ig-types";

import { HistogramMetricData } from "../types/analytics";

export const sortByOrder = (
  a: { order: number },
  b: { order: number }
): number => {
  if (a.order > b.order) {
    return 1;
  }
  if (a.order < b.order) {
    return -1;
  }
  return 0;
};

export const genericSortByOrder = <T extends { order?: number | undefined }>(
  a: T,
  b: T
): number => {
  if ((a.order || 0) > (b.order || 0)) {
    return 1;
  }
  if ((a.order || 0) < (b.order || 0)) {
    return -1;
  }
  return 0;
};

export const genericDescSort = <T>(a: T, b: T, sortVal?: string): number => {
  if (a[sortVal ?? "value"] < b[sortVal ?? "value"]) {
    return 1;
  }
  if (a[sortVal ?? "value"] > b[sortVal ?? "value"]) {
    return -1;
  }
  return 0;
};

export const genericAscSort = <T>(a: T, b: T, sortVal?: string): number => {
  if (a[sortVal ?? "value"] > b[sortVal ?? "value"]) {
    return 1;
  }
  if (a[sortVal ?? "value"] < b[sortVal ?? "value"]) {
    return -1;
  }
  return 0;
};

export const genericSort = <T>(a: T, b: T, sortVal: string): number => {
  if (a[sortVal] > b[sortVal]) {
    return 1;
  }
  if (a[sortVal] < b[sortVal]) {
    return -1;
  }
  return 0;
};

// export const personalizationMetricSort = <T extends { id: string }>(
//   experiences: T[],
//   data: MetricArrayType,
//   key: "visitors"
// ): T[] => {
//   return [...experiences].sort((a, b) => {
//     console.log("a", a);
//     console.log("da", data);
//     const aData = get(data, `data[${a.id}].visitors`, null);
//     console.log("ad", aData);
//     const bData = data.find((_) => _["EXPERIENCE_ID"] === b.id);
//     if (aData && bData) {
//       if (aData[key] > bData[key]) {
//         return -1;
//       }
//       if (aData[key] < bData[key]) {
//         return 1;
//       } else return 0;
//     } else if (aData && !bData) {
//       return -1;
//     } else if (!aData && bData) {
//       return 1;
//     } else {
//       return 0;
//     }
//   });
// };

export const genericBucketAscSort = <T extends { UNIT_BUCKET: number }>(
  a: T,
  b: T
): number => {
  if (a["UNIT_BUCKET"] > b["UNIT_BUCKET"]) {
    return 1;
  }
  if (a["UNIT_BUCKET"] < b["UNIT_BUCKET"]) {
    return -1;
  }
  return 0;
};

export const sortByBucket = (
  a: HistogramMetricData,
  b: HistogramMetricData
) => {
  return a.bucket - b.bucket;
};

export const customExclusionGroupSelectSort = <
  T extends { exclusionGroupName: string },
>(
  a: T,
  b: T
) => {
  // If both items have exclusionGroupName as "Non-Exclusive Tests", no sorting needed
  if (
    a.exclusionGroupName === "Non-Exclusive Tests" &&
    b.exclusionGroupName === "Non-Exclusive Tests"
  ) {
    return 0;
  }

  // If only one of the items has exclusionGroupName as "Non-Exclusive Tests", it should come first
  if (a.exclusionGroupName === "Non-Exclusive Tests") {
    return -1;
  }
  if (b.exclusionGroupName === "Non-Exclusive Tests") {
    return 1;
  }

  // If none of the items have exclusionGroupName as "Non-Exclusive Tests",
  // then sort alphabetically based on exclusionGroupName
  if (a.exclusionGroupName < b.exclusionGroupName) {
    return -1;
  }
  if (a.exclusionGroupName > b.exclusionGroupName) {
    return 1;
  }
  return 0;
};

export const overviewTableSortOrder = {
  all: 0,
  Desktop: 1,
  Mobile: 2,
  New: 3,
  Returning: 4,
  Direct: 5,
  Email: 6,
  Referral: 7,
  "Organic Social": 8,
  "Paid Social": 9,
  "Organic Search": 10,
  "Paid Search": 11,
  SMS: 12,
  "Other Channel": 13,
  Google: 14,
  Facebook: 15,
  Instagram: 16,
  TikTok: 17,
  Twitter: 18,
  Youtube: 19,
  Klaviyo: 20,
  "Other Traffic Source": 21,
  Other: 22,
};
export const overviewTableRowGroupSort = (data: any[], key: string): any[] => {
  return data.sort((a, b) => {
    return (
      overviewTableSortOrder[a[key] ?? "Other"] -
      overviewTableSortOrder[b[key] ?? "Other"]
    );
  });
};

export const customExclusionGroupListSort = (
  currentExperienceId: string | undefined,
  exclusionGroupEntities:
    | CreateExclusionGroupEntityFe[]
    | ExclusionGroupEntityDto[]
) => {
  const tempExclusionGroupEntities = [...exclusionGroupEntities];
  return tempExclusionGroupEntities.sort((a, b) => {
    if (a.experienceId === undefined && b.experienceId !== undefined) {
      return -1; // Put undefined entityId first
    } else if (a.experienceId !== undefined && b.experienceId === undefined) {
      return 1; // Put undefined entityId last
    } else if (
      a.experienceId === currentExperienceId &&
      b.experienceId !== currentExperienceId
    ) {
      return -1; // Put specified currentExperiment entityId first
    } else if (
      a.experienceId !== currentExperienceId &&
      b.experienceId === currentExperienceId
    ) {
      return 1; // Put specified string entityId last
    } else {
      return 0; // Maintain order
    }
  });
};
